import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { DollarSign, ExternalLink, Info, Users, X } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import { createPortalSession } from '../services/subscriptionService';
import SubscriptionPlansPage from './SubscriptionPlansPage';

interface ActiveTeam {
  team_id: number;
  team_name: string;
  league_start_date: string | null;
  league_end_date: string | null;
}

interface SubscriptionInfo {
  max_active_teams: number;
  active_teams_count: number;
  remaining_capacity: number;
  is_active: boolean;
  active_teams: ActiveTeam[];
  subscription_source?: string | null;
  billing_cycle?: string | null;
  stripe_cancel_at_period_end?: boolean | null;
  stripe_product_name?: string | null;
}

const SubscriptionPage: React.FC = () => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const subscriptionsEnabled = useFeatureFlag('enable-subscriptions', false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [deactivatingTeamId, setDeactivatingTeamId] = useState<number | null>(null);

  const fetchSubscriptionInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/users/me/profile');
      setSubscriptionInfo(response.data.subscription_info || null);
    } catch (error: any) {
      console.error('Error fetching subscription info:', error);
      showError('Failed to load subscription information');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  // Redirect to dashboard if subscriptions are disabled (after hooks)
  if (!subscriptionsEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const getPlanName = (maxTeams: number): string => {
    if (maxTeams === 1) return 'Single Team Captain';
    if (maxTeams === 3) return '3 Team Captain';
    if (maxTeams === 5) return '5 Team Captain';
    return `Custom (${maxTeams} teams)`;
  };

  const handleManageSubscription = async () => {
    try {
      const returnUrl = window.location.origin + '/subscription';
      const response = await createPortalSession(returnUrl);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error: any) {
      showError(error.message || 'Failed to open subscription management');
    }
  };

  const handleDeactivateTeam = async (teamId: number, teamName: string) => {
    if (!window.confirm(`Are you sure you want to remove yourself as captain of "${teamName}"? This will free up capacity in your subscription.`)) {
      return;
    }

    if (!user) {
      showError('You must be logged in to perform this action');
      return;
    }

    try {
      setDeactivatingTeamId(teamId);
      
      // Remove user as captain by updating their team member status
      await axios.patch(`/teams/${teamId}/members/${user.id}`, {
        is_captain: false,
      });
      
      success(`You have been removed as captain of "${teamName}"`);
      // Refresh subscription info to update the active teams list
      await fetchSubscriptionInfo();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to remove you as captain';
      
      // If they're the last captain, offer to delete the team instead
      if (errorMessage.includes('last captain') || errorMessage.includes('Cannot remove the last captain')) {
        const shouldDelete = window.confirm(
          `You are the last captain of "${teamName}". ` +
          `To remove yourself as captain, you need to delete the team. ` +
          `This will permanently delete the team and all its data. ` +
          `\n\nDo you want to delete the team?`
        );
        
        if (shouldDelete) {
          try {
            await axios.delete(`/teams/${teamId}`);
            success(`Team "${teamName}" has been deleted`);
            // Refresh subscription info to update the active teams list
            await fetchSubscriptionInfo();
          } catch (deleteError: any) {
            showError(deleteError.response?.data?.detail || deleteError.message || 'Failed to delete team');
          }
        }
      } else {
        showError(errorMessage);
      }
    } finally {
      setDeactivatingTeamId(null);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <LoadingSpinner />
      </div>
    );
  }

  // If user doesn't have a subscription at all, or subscription is inactive/expired, check if they have active teams
  if (!subscriptionInfo || subscriptionInfo.max_active_teams === 0 || !subscriptionInfo.is_active) {
    // If they have active teams but no subscription or expired subscription, show management UI with warning
    if (subscriptionInfo && subscriptionInfo.active_teams_count > 0) {
      // Show subscription management with warning about needing subscription
      return (
        <div>
          <Navigation />
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="px-4 py-6 sm:px-0">
              {/* Header with urgent warning */}
              <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Subscription Required</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">You need a subscription to manage teams</p>
                <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                        {!subscriptionInfo.is_active && subscriptionInfo.max_active_teams > 0 
                          ? 'Subscription Expired - Access Blocked'
                          : 'No Subscription - Access Blocked'}
                      </div>
                      <div className="text-sm text-red-700 dark:text-red-400">
                        {!subscriptionInfo.is_active && subscriptionInfo.max_active_teams > 0 ? (
                          <>
                            Your subscription has expired or been cancelled. You are currently a captain of {subscriptionInfo.active_teams_count} active team{subscriptionInfo.active_teams_count !== 1 ? 's' : ''}. 
                            Please remove yourself as captain from all teams below, or reactivate your subscription to continue managing teams.
                          </>
                        ) : (
                          <>
                            You are currently a captain of {subscriptionInfo.active_teams_count} active team{subscriptionInfo.active_teams_count !== 1 ? 's' : ''}, 
                            but you don't have an active subscription. Please remove yourself as captain from all teams below, 
                            or subscribe to a plan to continue managing teams.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Teams List */}
              {subscriptionInfo.active_teams && subscriptionInfo.active_teams.length > 0 && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Active Teams ({subscriptionInfo.active_teams.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {subscriptionInfo.active_teams.map((team) => (
                      <div
                        key={team.team_id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {team.team_name}
                          </div>
                          {team.league_start_date && team.league_end_date && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {new Date(team.league_start_date).toLocaleDateString()} - {new Date(team.league_end_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleDeactivateTeam(team.team_id, team.team_name)}
                          disabled={deactivatingTeamId === team.team_id}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {deactivatingTeamId === team.team_id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              <span>Removing...</span>
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-2" />
                              Remove Me as Captain
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reactivate or Subscribe */}
              {!subscriptionInfo.is_active && subscriptionInfo.subscription_source === 'stripe' && subscriptionInfo.max_active_teams > 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Reactivate Your Subscription</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Your subscription has expired. You can reactivate it through the Stripe customer portal.
                  </p>
                  <Button
                    onClick={handleManageSubscription}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                </div>
              ) : null}

              {/* Subscription Plans */}
              <div className="mt-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {!subscriptionInfo.is_active && subscriptionInfo.subscription_source === 'stripe' && subscriptionInfo.max_active_teams > 0
                    ? 'Or Subscribe to a New Plan'
                    : 'Subscribe to Continue'}
                </h2>
                <SubscriptionPlansPage showNavigation={false} />
              </div>
            </div>
          </div>
        </div>
      );
    }
    // No subscription and no active teams - show pricing table
    return <SubscriptionPlansPage showNavigation={true} />;
  }

  // User has an active subscription - show management UI
  return (
    <div>
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Subscription Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your subscription and team capacity</p>
            {subscriptionInfo.active_teams_count > subscriptionInfo.max_active_teams && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                      Over Capacity - Access Blocked
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-400">
                      You have {subscriptionInfo.active_teams_count} active teams but your plan only allows {subscriptionInfo.max_active_teams} team{subscriptionInfo.max_active_teams !== 1 ? 's' : ''}. 
                      Please remove yourself as captain from {subscriptionInfo.active_teams_count - subscriptionInfo.max_active_teams} team{subscriptionInfo.active_teams_count - subscriptionInfo.max_active_teams !== 1 ? 's' : ''} below, 
                      or upgrade your subscription to increase your plan capacity.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subscription Status Card */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Subscription</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Plan details and usage</p>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="mb-6">
              {subscriptionInfo.is_active ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                  Active Subscription
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                  Inactive Subscription
                </span>
              )}
            </div>

            {/* Plan Name */}
            <div className="mb-6">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Current Plan
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {subscriptionInfo.stripe_product_name || getPlanName(subscriptionInfo.max_active_teams)}
              </div>
              {subscriptionInfo.billing_cycle && !subscriptionInfo.stripe_product_name && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Billed {subscriptionInfo.billing_cycle === 'annual' ? 'annually' : 'monthly'}
                </div>
              )}
            </div>

            {/* Capacity Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Active Teams
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {subscriptionInfo.active_teams_count}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Plan Capacity
                </div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {subscriptionInfo.max_active_teams}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Remaining Capacity
                </div>
                <div className={`text-2xl font-semibold ${
                  subscriptionInfo.remaining_capacity > 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {subscriptionInfo.remaining_capacity}
                </div>
              </div>
            </div>

            {/* Active Teams List */}
            {subscriptionInfo.active_teams && subscriptionInfo.active_teams.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Active Teams ({subscriptionInfo.active_teams.length})
                  </h3>
                </div>
                <div className="space-y-3">
                  {subscriptionInfo.active_teams.map((team) => (
                    <div
                      key={team.team_id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {team.team_name}
                        </div>
                        {team.league_start_date && team.league_end_date && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {new Date(team.league_start_date).toLocaleDateString()} - {new Date(team.league_end_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => handleDeactivateTeam(team.team_id, team.team_name)}
                        disabled={deactivatingTeamId === team.team_id}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {deactivatingTeamId === team.team_id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            <span>Removing...</span>
                          </>
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-2" />
                            Remove Me as Captain
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manage Subscription Button */}
            {subscriptionInfo.subscription_source === 'stripe' && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={handleManageSubscription}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Manage Subscription
                </Button>
                {subscriptionInfo.stripe_cancel_at_period_end && (
                  <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-800 dark:text-amber-300">
                        Your subscription is set to cancel at the end of the current billing period.
                        You can reactivate it anytime before then.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;

